import '@kerfjs/ui/select/register';

import { Button } from '@kerfjs/ui/button';
import { List } from '@kerfjs/ui/list';
import { Select } from '@kerfjs/ui/select';
import { DialogSurface } from '@kerfjs/ui/surface-scaffold';
import { TextField } from '@kerfjs/ui/text-field';
import type { SafeHtml } from 'kerfjs';

import { DECISION_LABELS } from '../../../components/decision-badge.js';
import type { LineOrderT } from '../../../data/api.js';
import {
  analysisReport,
  decisionReason,
  decisionVendor,
  lineDecisionDialog,
  quoteMatrix,
  SELECTION_REASONS,
  workspace,
} from '../../../data/state.js';
import { formatCurrencyText } from '../../../lib/currency-format.js';

/** The price/note dialog for decision actions that need a price (approve /
 *  negotiate), for a single line or a batch (HS2-1F4215). */
export function DecisionDialog(): SafeHtml {
  const d = lineDecisionDialog.value;
  if (d === null) return <></>;
  const label = DECISION_LABELS[d.action] ?? d.action;
  const many = d.lineIds.length > 1;
  const heading = `${label}${many ? ` · ${String(d.lineIds.length)} lines` : ''}`;
  const priceLabel = d.action === 'approve' ? 'Approved Unit Price' : 'Target Price';
  // For a single line, let the reviewer choose the vendor/quote (default: the
  // cheapest delivered quote) so savings roll up by vendor (HS2-ZP1D0Z).
  const m = quoteMatrix.value;
  const singleLineId = !many ? d.lineIds[0] : undefined;
  const failedVendors = new Set(
    ((workspace.value?.failedOrders ?? []) as LineOrderT[])
      .filter((order) => order.lineItemId === singleLineId && order.vendorId !== null)
      .map((order) => order.vendorId ?? ''),
  );
  const vendorOptions =
    singleLineId !== undefined && m !== null
      ? m.columns
          .map((col) => ({ col, cell: m.cells[singleLineId]?.[col.quotationId] }))
          .filter(
            (x): x is { col: typeof x.col; cell: NonNullable<typeof x.cell> } =>
              x.cell != null && x.cell.basePrice != null,
          )
          // Vendors whose order for this line failed rank last, so the next-best quote is the
          // default while re-sourcing (FR-5.21, HS2-SFY5DY).
          .sort(
            (a, b) =>
              Number(failedVendors.has(a.col.vendorId ?? '')) - Number(failedVendors.has(b.col.vendorId ?? '')) ||
              (a.cell.basePrice ?? 0) - (b.cell.basePrice ?? 0),
          )
      : [];
  // Pre-fill the price when possible (HS2-DFWA5N): approve → the chosen (default
  // best) quote's unit price; negotiate → the analysis's recommended target for
  // the line, falling back to the best quote price. Single line only. The bound
  // value re-derives if the reviewer switches vendor (approve tracks the vendor);
  // an unchanged value lets a manual edit stand through re-renders.
  const bestPrice = vendorOptions[0]?.cell.basePrice ?? null;
  const chosenPrice =
    vendorOptions.find((x) => x.cell.quoteLineId === decisionVendor.value)?.cell.basePrice ?? bestPrice;
  const verdict =
    singleLineId !== undefined ? analysisReport.value?.verdicts.find((v) => v.lineItemId === singleLineId) : undefined;
  const prefillPrice =
    d.action === 'approve'
      ? chosenPrice != null
        ? String(chosenPrice)
        : ''
      : (verdict?.targetPrice ?? (bestPrice != null ? String(bestPrice) : ''));
  return (
    <DialogSurface
      label={heading}
      className="decision-dialog"
      rootAttributes={{ 'data-unsaved-guard': 'this decision', 'data-auto-open-dialog': '' }}
      footer={
        <>
          <Button
            tone="neutral"
            size="default"
            emphasis="plain"
            action="decision-dialog-cancel"
            rootAttributes={{ 'data-unsaved-cancel': '', 'data-dialog-close': '' }}
          >
            Cancel
          </Button>
          <Button
            size="default"
            tone="brand"
            emphasis="primary"
            action="decision-dialog-save"
            rootAttributes={{ 'data-unsaved-save': '' }}
          >
            Save Decision
          </Button>
        </>
      }
    >
      <List gap="m">
        <TextField
          name="line-decide-price"
          type="number"
          label={priceLabel}
          placeholder="unit price"
          value={prefillPrice}
          autofocus
        />
        {(() => {
          if (vendorOptions.length === 0) return null;
          const bestQuoteLineId = vendorOptions[0]?.cell.quoteLineId ?? '';
          const chosenVendor = decisionVendor.value ?? bestQuoteLineId;
          // Choosing a non-best (not lowest) quote asks why, so the exception is
          // recorded (HS2-EKEE9S); "Other" reveals a free-text field. The selects
          // bind to signals so a re-render doesn't reset the reviewer's choice.
          const isNonBest = vendorOptions.length > 1 && chosenVendor !== '' && chosenVendor !== bestQuoteLineId;
          const reasonOther = decisionReason.value === 'Other';
          return [
            <Select
              name="line-decide-vendor"
              label="Chosen Vendor"
              value={chosenVendor}
              choices={vendorOptions.map(({ col, cell }, i) => ({
                value: cell.quoteLineId,
                label: `${col.vendorName} · ${formatCurrencyText(cell.basePrice, m?.base ?? 'PKR')}${
                  failedVendors.has(col.vendorId ?? '') ? ' · order failed' : i === 0 ? ' · best' : ''
                }`,
              }))}
            />,
            <input type="hidden" name="line-decide-best-quote" value={bestQuoteLineId} />,
            isNonBest ? (
              <Select<string>
                name="line-decide-reason"
                label="Reason for Not Choosing the Lowest Quote"
                value={decisionReason.value}
                choices={[
                  { value: '', label: '— Choose a Reason —' },
                  ...SELECTION_REASONS.map((r) => ({ value: r, label: r })),
                ]}
              />
            ) : null,
            isNonBest && reasonOther ? (
              <TextField
                type="text"
                name="line-decide-reason-other"
                label="Reason (Other)"
                placeholder="Describe the reason"
              />
            ) : null,
          ];
        })()}
        <TextField type="text" name="line-decide-note" label="Note" placeholder="optional note" />
      </List>
    </DialogSurface>
  );
}
